import type { AnnotatorPreferences } from "@/types/annotation";
import { CallRingPreference } from "@/types/enums/call-ring-preference";
import { SelfInvitePolicy } from "@/types/enums/google-calendar-invite-policy";
import {
  NotificationPreferencesData,
  NotificationPreferencesRequest,
  NotificationPreset,
  NotificationPresetsData,
} from "@/types/notification";
import {
  OverviewLayoutResponse,
  OverviewSectionPreference,
} from "@/types/overview-layout";
import { PortalDashboardConfig } from "@/types/portal-dashboard";
import type {
  CalendarTextSize,
  CheckoutAppointmentSyncPreference,
} from "@/types/provider";
import { PatientPortalConfig } from "@/types/tenant";

import { requestData } from "@/api/api-config";

export async function updateShowIds(showIds: boolean): Promise<boolean | null> {
  return requestData<boolean>(`admin/settings/show-ids/${showIds}`, "put");
}

export async function updateShowPatientIds(
  showPatientIds: boolean,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/show-patient-ids/${showPatientIds}`,
    "put",
    undefined,
    false,
  );
}

export async function updateShowWeekendsOnCalendar(
  showWeekendsOnCalendar: boolean,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/show-weekends-on-calendar/${showWeekendsOnCalendar}`,
    "put",
  );
}

export async function updateMarkChatReadOnOpen(
  value: boolean,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/mark-chat-read-on-open/${value}`,
    "put",
  );
}

export async function updateShowMessagesBox(
  showMessagesBox: boolean,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/show-messages-box/${showMessagesBox}`,
    "put",
  );
}

export async function updateAdjustCalendarHours(
  startHour: number,
  endHour: number,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/adjust-calendar-hours/${startHour}/${endHour}`,
    "put",
  );
}

export async function updateCalendarProviderOrder(
  providerIds: string[],
): Promise<string[] | null> {
  return requestData<string[]>(
    "admin/settings/calendar-provider-order",
    "put",
    {
      json: { providerIds },
    },
  );
}

/** Partial update: only the settings passed change; null resets one to the default. */
export async function updateAnnotatorPreferences(
  preferences: AnnotatorPreferences,
): Promise<AnnotatorPreferences | null> {
  return requestData<AnnotatorPreferences>(
    "admin/settings/annotator-preferences",
    "patch",
    { json: preferences },
  );
}

export async function getNotificationPreferences(): Promise<NotificationPreferencesData | null> {
  return requestData<NotificationPreferencesData>(
    "admin/settings/notification-preferences",
    "get",
    {},
    false,
  );
}

export async function createNotificationPreferences(
  request: NotificationPreferencesRequest,
): Promise<NotificationPreferencesData | null> {
  return requestData<NotificationPreferencesData>(
    "admin/settings/notification-preferences",
    "post",
    { json: request },
    false,
  );
}

export async function getNotificationPresets(): Promise<NotificationPresetsData | null> {
  return requestData<NotificationPresetsData>(
    "admin/settings/notification-preferences/presets",
    "get",
    {},
    false,
  );
}

/** Replaces the user's in-app subscriptions with the level's set; text and email stay. */
export async function applyNotificationPreset(
  preset: NotificationPreset,
): Promise<NotificationPreferencesData | null> {
  return requestData<NotificationPreferencesData>(
    "admin/settings/notification-preferences/preset",
    "post",
    { json: { preset } },
    false,
  );
}

export async function deleteNotificationPreference(
  preferenceId: string,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/notification-preferences/${preferenceId}`,
    "delete",
    {},
    false,
  );
}

export async function bulkDeleteNotificationPreferences(
  preferenceIds: string[],
): Promise<boolean | null> {
  return requestData<boolean>(
    "admin/settings/notification-preferences/bulk",
    "delete",
    { json: { preferenceIds } },
    false,
  );
}

export async function updateShowOverdueToasts(
  showOverdueToasts: boolean,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/show-overdue-toasts/${showOverdueToasts}`,
    "put",
  );
}

export async function updateOnlyMyAppointmentNotifications(
  value: boolean,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/only-my-appointment-notifications/${value}`,
    "put",
  );
}

export async function updateCallRingPreference(
  value: CallRingPreference | null,
): Promise<boolean | null> {
  return requestData<boolean>("admin/settings/call-ring-preference", "put", {
    json: { callRingPreference: value },
  });
}

export async function updateNotificationAutoMarkRead(
  value: string,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/notification-auto-mark-read/${value}`,
    "put",
  );
}

export async function updateNotificationSoundPreference(
  value: string,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/notification-sound-preference/${value}`,
    "put",
  );
}

export async function updateSendGcalInvitesSelf(
  policy: SelfInvitePolicy | null,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/send-gcal-invites-self/${policy ?? "null"}`,
    "put",
  );
}

export async function updateCalendarGridInterval(
  gridInterval: number,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/calendar-grid-interval/${gridInterval}`,
    "put",
    {},
    false,
  );
}

export async function updateShowTranslucentPastEvents(
  showTranslucentPastEvents: boolean,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/show-translucent-past-events/${showTranslucentPastEvents}`,
    "put",
  );
}

export async function updateAppointmentTimeStep(
  stepMinutes: number | null,
): Promise<boolean | null> {
  const endpoint =
    stepMinutes !== null
      ? `admin/settings/appointment-time-step/${stepMinutes}`
      : `admin/settings/appointment-time-step/null`;
  return requestData<boolean>(endpoint, "put", {}, false);
}

export async function updateCalendarTextSize(
  value: CalendarTextSize,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/calendar-text-size/${value}`,
    "put",
    undefined,
    false,
  );
}

export async function updateCheckoutAppointmentSyncPreference(
  value: CheckoutAppointmentSyncPreference,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/checkout-appointment-sync-preference/${value}`,
    "put",
    undefined,
    false,
  );
}

export async function updateShowGhostEvent(
  showGhostEvent: boolean,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/show-ghost-event/${showGhostEvent}`,
    "put",
  );
}

export async function updateAutoFilterNotesToSelf(
  autoFilter: boolean,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/auto-filter-notes-to-self/${autoFilter}`,
    "put",
  );
}

export async function updateAutoSignOffNotes(
  autoSignOff: boolean,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/auto-sign-off-notes/${autoSignOff}`,
    "put",
  );
}

export async function updateShowDeletedProviders(
  showDeletedProviders: boolean,
): Promise<boolean | null> {
  return requestData<boolean>("admin/settings/show-deleted-providers", "put", {
    json: { showDeletedProviders },
  });
}

export async function updateHideSupportChat(
  hideSupportChat: boolean,
): Promise<boolean | null> {
  return requestData<boolean>(
    `admin/settings/hide-support-chat/${hideSupportChat}`,
    "put",
  );
}

export async function updateTimezoneOverride(
  value: string | null,
): Promise<boolean | null> {
  const encoded = value !== null ? encodeURIComponent(value) : "null";
  return requestData<boolean>(
    `admin/settings/timezone-override/${encoded}`,
    "put",
  );
}

export async function updatePortalDashboardConfig(
  config: PortalDashboardConfig,
): Promise<boolean | null> {
  return requestData<boolean>("admin/settings/portal-dashboard-config", "put", {
    json: { widgets: config.widgets },
  });
}

export async function updatePatientPortalConfig(
  config: Partial<PatientPortalConfig>,
): Promise<PatientPortalConfig | null> {
  return requestData<PatientPortalConfig>(
    "admin/settings/patient-portal-config",
    "put",
    { json: config },
  );
}

export async function updateOverviewLayout(
  sections: OverviewSectionPreference[],
): Promise<OverviewLayoutResponse | null> {
  return requestData<OverviewLayoutResponse>(
    "admin/settings/overview-layout",
    "put",
    { json: { sections } },
  );
}
